import { ModularCard } from '@/components/ui/custom/modular/ModularCard';
import SecondaryInput from '@/components/ui/custom/secondary/SecondaryInput';
import useToggle from '@/data/hook/useToggle';
import { bodyTextSmallest } from '@/styles/styles';
import { validateLink } from '@/utils/validators/regex';
import { Control, useController } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { twMerge, twJoin } from 'tailwind-merge';
import { LessonFields } from './LessonBoxForm';
import Text from '@/components/ui/custom/dataDisplay/Text';

type LinksSectionProps = {
  control: Control<LessonFields>;
};

const LinksSection = ({ control }: LinksSectionProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'lessonForm',
  });

  const pattern = {
    value: validateLink,
    message: t('warnings.validateLink'),
  };

  const toolField = useController({
    name: 'toolUrl',
    control: control,
    rules: {
      pattern,
    },
  });

  const instructionField = useController({
    name: 'instructionsUrl',
    control: control,
    rules: {
      pattern,
    },
  });
  const { isOpen, toggle } = useToggle();

  const hasError =
    toolField.fieldState.error || instructionField.fieldState.error;

  return (
    <ModularCard>
      <ModularCard.Header
        title={t('links')}
        isOpen={isOpen}
        onOpen={toggle}
        titleFallback={
          <Text
            text={t('links')}
            className={twMerge('!text-14 font-500', hasError && 'text-error')}
          />
        }
      />

      <ModularCard.Accordion isOpen={isOpen}>
        <div className="flex flex-col gap-3.5 mx-3 mb-2">
          <div className="flex flex-col">
            <SecondaryInput
              testId="toolUrlInput"
              label={t('toolUrl')}
              error={toolField.fieldState.error?.message}
              {...toolField.field}
              value={toolField.field.value ?? undefined}
            />
            <p className={twJoin(bodyTextSmallest, 'text-neutral')}>
              {t('toolLinkMessage')}
            </p>
          </div>

          <div className="flex flex-col gap-0">
            <SecondaryInput
              testId="instructionsUrlInput"
              label={t('instructionsUrl')}
              error={instructionField.fieldState.error?.message}
              {...instructionField.field}
              value={instructionField.field.value ?? undefined}
            />
            <p className={twJoin(bodyTextSmallest, 'text-neutral')}>
              {t('videoInstallMessage')}
            </p>
          </div>
        </div>
      </ModularCard.Accordion>
    </ModularCard>
  );
};

export default LinksSection;
